// bluaIQ — TASK-WEB-1: website-safe read-only Takeoff payload contract v2 (types +
// defensive validator), ported 1:1 from the accepted TAKEOFF reference implementation
// (work_takeoff_repo, TAKEOFF-C1800-WEBSITE-PAYLOAD-CONTRACT-V2-27, commit 1121d1e,
// Codex ACCEPTED 2026-07-08, takeoff_system/website_payload_contract_v2.py).
//
// TYPES + VOCABULARY ONLY — no project data, no I/O, no React. Reads no benchmark data.
// Contract v2 extends the frozen v1 read-only contract with EXACTLY ONE new allowed
// section (quantification_lines_candidate_only) while carrying the entire v1 forbidden
// list unchanged and adding order-price / supplier-offer / raw-OCR-text forbidden
// families.
//
// The validator is DEFENSIVE: a payload is rejected if any forbidden key appears
// ANYWHERE in its tree (deep scan, not top-level-only), if a quantification line
// claims to be purchasable, or if identity/provenance/bilingual requirements are
// missing. Nothing validated here ever authorizes a purchase.

export const CONTRACT_VERSION = 'v2-website-safe-readonly-takeoff-payload-contract-v2'
export const BASED_ON_CONTRACT_VERSION = 'v2-website-safe-readonly-takeoff-payload-contract-v1'

export const NEW_ALLOWED_SECTION = 'quantification_lines_candidate_only'

export const QUANTIFICATION_STATUSES = [
  'quantity_confirmed_from_plan',
  'quantity_reading_in_progress',
  'quantity_estimated_from_user_budget',
  'spec_present_quantity_pending',
] as const

export type QuantificationStatus = (typeof QUANTIFICATION_STATUSES)[number]

// v1 forbidden payload sections/fields — carried UNCHANGED.
const V1_FORBIDDEN_FIELDS = [
  'accepted_final_quantity', 'final_quantity', 'preview_quantity',
  'purchase_quantity', 'purchase_bom', 'BOM', 'BOQ', 'final_geometry',
  'final_room_polygon', 'final_opening_location', 'host_wall_assignment',
  'opening_deduction', 'graph_edge_finalization', 'schedule_grounding_result',
  'finish_material_final_linkage', 'app_mutation_instruction',
  'database_write_instruction',
] as const

// v2 additions. OCR key list deliberately WIDE (Codex TASK-27 audit): the principal
// risk is raw OCR text reaching the client under another name, so false positives are
// accepted over false negatives.
const V2_ADDITIONAL_FORBIDDEN_FIELDS = [
  'order_price', 'order_prices', 'supplier_offer_amount',
  'supplier_offer_amounts', 'ocr_full_text', 'raw_ocr_text',
  'ocr_full_text_preserved', 'ocr_text', 'full_ocr_text', 'raw_text',
  'full_text', 'extracted_text', 'recognized_text', 'page_text',
] as const

export const FORBIDDEN_FIELDS: readonly string[] = [...V1_FORBIDDEN_FIELDS, ...V2_ADDITIONAL_FORBIDDEN_FIELDS]

export const REQUIRED_IDENTITY_FIELDS = ['sourceDocumentHash', 'read_run_id'] as const

export const REQUIRED_LINE_FIELDS = [
  'section', 'item_description', 'quantity_value', 'quantity_unit',
  'quantification_status', 'sources', 'notes',
] as const

// Flags every quantification line MUST carry with these exact values.
export const MANDATORY_LINE_FLAG_VALUES = {
  packaging_only: true,
  purchase_ready: false,
  is_purchase_quantity: false,
  is_order_price_bearing: false,
} as const

export const REQUIRED_WARNING_ENUMS = [
  'candidate_only_not_purchase_ready',
  'no_final_quantity',
  'no_purchase_bom_boq',
  'not_an_order_price',
  'website_readonly_display',
] as const

export const MANDATORY_GUARDS = [
  'ocr_text_redaction_before_ui',
  'not_purchase_ready_posture',
  'evidence_classes_never_merged',
  'bilingual_formal_labels',
  'benchmark_isolation',
] as const

// Line-scoped money-string guard (Codex TASK-27 audit): inside displayed
// quantification lines ANY monetary-looking string could read as a price, offer, or
// purchase decision — rejected regardless of which key carries it. Deliberately NOT
// applied payload-wide to avoid label false positives.
// Exported (like the Python reference's _MONEY_STRING_RX) so the additive v3
// module can reuse it without modifying this validator.
export const MONEY_STRING_RX = /\$\s*\d[\d,]*(?:\.\d{1,2})?/

export interface BilingualLabel {
  en: string
  es: string
}

export interface QuantificationLineSource {
  evidence_class: string
  ref: string
  trust_tier?: string
  classification?: string
  designation?: string
  source_page?: string
}

export interface QuantificationLine {
  section: string
  item_description: string
  quantity_value: number | null
  quantity_unit: string
  quantification_status: QuantificationStatus
  sources: QuantificationLineSource[]
  notes: string
  packaging_only: true
  purchase_ready: false
  is_purchase_quantity: false
  is_order_price_bearing: false
  /** v4 payloads: marks MEP guide/proposal lines ("MEP — …" sections). These are NEVER
   *  material quantities and must render apart from them. Type-level only — the
   *  validator's rules are unchanged (the key is not forbidden and carries no
   *  purchase semantics). */
  mep_guide_only?: true
  /** v11.6 topology fields (Excel export v2 line-role routing). ADDITIVE and
   *  type-only — the validator is unchanged (none is a forbidden key, none
   *  carries purchase semantics; verified: the three captured production payloads
   *  pass validatePayloadV3 with these present). Same additive discipline as
   *  `mep_guide_only` above.
   *
   *  `line_role` classifies a line for sheet routing WITHOUT prose matching:
   *    - `summary`    a totalizable partida; may have detail children rolling up
   *    - `detail`     backs a summary — `rolls_up_to` = that summary's `line_id`
   *    - `standalone` a one-line partida with no children
   *  `line_id` is the stable join anchor and is populated ONLY on summary lines
   *  (detail/standalone carry `null`); `rolls_up_to` is the STRUCTURAL join key
   *  (never a label). `client_blocking_kind` names why a line is blocked pending
   *  client input, and is ORTHOGONAL to measurement — a line can carry a
   *  candidate quantity and still be blocked. Absent `line_role` (older payloads)
   *  is treated as `standalone` by the exporter so no line is ever dropped. */
  line_id?: string | null
  line_role?: 'summary' | 'detail' | 'standalone'
  rolls_up_to?: string | null
  client_blocking_kind?: 'plan_missing_information' | 'lane_not_available' | null
  /** v11.7 emitter re-capture (SG): the STRUCTURAL link from a blocked line to the
   *  specific `insulation_lane.client_questions[]` that block it, by `question_id`.
   *  ADDITIVE, type-only — not a forbidden key, no purchase semantics (verified:
   *  the re-captured SG payload passes validatePayloadV3 with it present). Lets the
   *  exporter render "blocked — pending these N questions" instead of the generic
   *  "trade not available" contradiction, WITHOUT inferring from prose. Absent on
   *  lines with no linked questions. */
  client_question_ids?: string[]
  /** VAULTED-P1 recapture (engine 88c9efc, fixture sha 1f1eecff…): the three
   *  line-blocking facets, ADDITIVE and PORTED FROM THE REAL FIXTURE — never
   *  invented. `two_axis_state` is deliberately ABSENT: the engine withdrew it
   *  from these facets (Codex: hotfix separated from the first v2 emission);
   *  nothing here may build on it until the two-axis front un-parks.
   *
   *  `missing_input_request` names WHAT the engine needs to measure the line
   *  (bilingual prose + plan-text evidence; `kind`/`resolution_owner` are
   *  internal routing tokens and never reach the client DOM).
   *  `diagnostic_projection` is an OPERAND, not a quantity — its own `role`
   *  prose says so — and must never render as a measurement or enter any
   *  roll-up (it is EXCLUDED from the client projection outright).
   *  `chained_to_line_ids` is the structural link from a dependent blocked
   *  line to the summary lines it waits on (same join family as
   *  `client_question_ids`). */
  missing_input_request?: MissingInputRequest | null
  diagnostic_projection?: DiagnosticProjection | null
  chained_to_line_ids?: string[] | null
}

export interface MissingInputRequestEvidence {
  page: number
  text_as_found: string
}

export interface MissingInputRequest {
  /** Internal classification token (e.g. `geometry_linkage`) — auditor-only. */
  kind: string
  /** Bilingual prose describing the needed input — client-safe. */
  needed_input: string
  /** Internal routing token (e.g. `engine_geometry`) — auditor-only. */
  resolution_owner: string
  evidence: MissingInputRequestEvidence[]
}

export interface DiagnosticProjection {
  /** The engine's own prose contract: this number is a diagnostic operand,
   *  NOT a quantity, and participates in NO roll-up. */
  role: string
  /** Facet-specific named operands (e.g. `flat_ceiling_projection_sf`). */
  [operand: string]: number | string
}

export interface WebsiteTakeoffPayloadV2 {
  sourceDocumentHash: string
  read_run_id: string
  website_readiness_status: string
  warnings: string[]
  labels: Record<string, BilingualLabel>
  quantification_lines_candidate_only: QuantificationLine[]
  safe_next_action: string
}

export interface ValidationResult {
  valid: boolean
  violations: string[]
  contract_version: string
}

function* iterKeys(node: unknown): Generator<string> {
  if (Array.isArray(node)) {
    for (const item of node) yield* iterKeys(item)
  } else if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      yield key
      yield* iterKeys(value)
    }
  }
}

// Exported (like the Python reference's _iter_strings) for the additive v3 module.
export function* iterStrings(node: unknown): Generator<string> {
  if (typeof node === 'string') {
    yield node
  } else if (Array.isArray(node)) {
    for (const item of node) yield* iterStrings(item)
  } else if (node && typeof node === 'object') {
    for (const value of Object.values(node as Record<string, unknown>)) yield* iterStrings(value)
  }
}

/**
 * Validate a website-bound read-only payload against contract v2.
 * Never mutates input. Mirrors validate_payload_v2() in the accepted Python reference.
 */
export function validatePayloadV2(payload: unknown): ValidationResult {
  const violations: string[] = []

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { valid: false, violations: ['payload_not_object'], contract_version: CONTRACT_VERSION }
  }
  const p = payload as Record<string, unknown>

  for (const field of REQUIRED_IDENTITY_FIELDS) {
    if (!p[field]) violations.push(`missing_identity_field:${field}`)
  }

  const forbiddenPresent = new Set<string>()
  for (const key of iterKeys(p)) {
    if (FORBIDDEN_FIELDS.includes(key)) forbiddenPresent.add(key)
  }
  for (const key of Array.from(forbiddenPresent).sort()) {
    violations.push(`forbidden_field_present:${key}`)
  }

  const rawLines = p[NEW_ALLOWED_SECTION]
  const lines: Record<string, unknown>[] | null = Array.isArray(rawLines)
    ? (rawLines as Record<string, unknown>[])
    : null
  if (lines === null) {
    violations.push(`missing_section:${NEW_ALLOWED_SECTION}`)
  } else {
    lines.forEach((line, i) => {
      for (const field of REQUIRED_LINE_FIELDS) {
        if (!(field in line)) violations.push(`line[${i}].missing_field:${field}`)
      }
      const status = line.quantification_status
      if (!QUANTIFICATION_STATUSES.includes(status as QuantificationStatus)) {
        violations.push(`line[${i}].invalid_status:${String(status)}`)
      }
      if (status === 'quantity_confirmed_from_plan' && (line.quantity_value === null || line.quantity_value === undefined)) {
        violations.push(`line[${i}].confirmed_without_quantity`)
      }
      for (const [flag, expected] of Object.entries(MANDATORY_LINE_FLAG_VALUES)) {
        if (line[flag] !== expected) violations.push(`line[${i}].flag_violation:${flag}`)
      }
      if (!Array.isArray(line.sources) || (line.sources as unknown[]).length === 0) {
        violations.push(`line[${i}].missing_provenance_sources`)
      }
      for (const text of iterStrings(line)) {
        const match = MONEY_STRING_RX.exec(text)
        if (match) {
          violations.push(`line[${i}].money_string_present:${match[0]}`)
          break
        }
      }
    })
  }

  const warnings = new Set(Array.isArray(p.warnings) ? (p.warnings as string[]) : [])
  for (const warning of REQUIRED_WARNING_ENUMS) {
    if (!warnings.has(warning)) violations.push(`missing_required_warning:${warning}`)
  }

  const labels = p.labels && typeof p.labels === 'object' && !Array.isArray(p.labels)
    ? (p.labels as Record<string, unknown>)
    : {}
  for (const [key, pair] of Object.entries(labels)) {
    const bp = pair as { en?: unknown; es?: unknown } | null
    if (!bp || typeof bp !== 'object' || !bp.en || !bp.es) {
      violations.push(`label_not_bilingual:${key}`)
    }
  }

  return { valid: violations.length === 0, violations, contract_version: CONTRACT_VERSION }
}
